import React from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Text, useMediaQuery, useTheme } from '@chakra-ui/react';
import { curryRight } from 'lodash-es';
import { translateModel } from '../../../../../utils/translate';
import { User as UserIcon } from '../../../../context/Chakra/Icons';
import { useAppContext } from '../../../context/appContext';
import { useProfileContext } from '../context/profileContext';

const REWARD_ON_GENERAL_FIELDS = {
  name: 'member_profile.member_info.name',
  email: 'member_profile.member_info.email',
  mobile_phone: 'member_profile.member_info.mobile',
  gender: 'member_profile.member_info.gender',
  birthday: 'member_profile.member_info.birthday',
};

const SUBSCRIPTION_REWARD_ON = {
  email: 'member_profile.member_info.subscription.email',
  sms: 'member_profile.member_info.subscription.sms',
  line: 'member_profile.member_info.subscription.line',
  facebook: 'member_profile.member_info.subscription.facebook',
  whatsapp: 'member_profile.member_info.subscription.whatsapp',
};

const getMemberInfoRewardDescription = (
  memberInfoRewardInfo,
  t,
  translateModel,
  hasMemberInfoRewardSubscription = false,
) => {
  const {
    custom_fields,
    member_points,
    promotion_ids,
    reward_on,
    user_credits,
    subscription_reward_on = [],
  } = memberInfoRewardInfo;
  const generalFields = reward_on.map((reward) =>
    t(REWARD_ON_GENERAL_FIELDS[reward]),
  );
  const customerFields =
    custom_fields
      ?.filter((field) => field.member_info_reward === 'true')
      .map((field) => translateModel(field.name_translations)) || [];
  const rewards = [];
  if (user_credits) {
    rewards.push(
      t('member_profile.member_info.user_credits', {
        count: user_credits,
      }),
    );
  }
  if (member_points) {
    rewards.push(
      t('member_profile.member_info.member_points', {
        count: member_points,
      }),
    );
  }
  if (promotion_ids?.length > 0) {
    rewards.push(
      t('member_profile.member_info.coupons', {
        count: promotion_ids.length,
      }),
    );
  }
  const subscriptionFields = hasMemberInfoRewardSubscription
    ? subscription_reward_on.map((subscription) =>
        t(SUBSCRIPTION_REWARD_ON[subscription]),
      )
    : [];
  const getI18nKey = (hasConditionFields, hasSubscriptions) => {
    if (hasConditionFields && hasSubscriptions) {
      return 'member_profile.member_info.hint.both';
    }
    if (hasSubscriptions) {
      return 'member_profile.member_info.hint.subscriptions_only';
    }
    return 'member_profile.member_info.hint.fields_only';
  };

  const symbol = t('member_profile.member_info.symbol');
  const hasConditionFields = generalFields.concat(customerFields).length > 0;
  const hasSubscriptions = subscriptionFields.length > 0;

  return t(getI18nKey(hasConditionFields, hasSubscriptions), {
    rewards: rewards.join(symbol),
    ...(hasConditionFields && {
      fields: generalFields.concat(customerFields).join(symbol),
    }),
    ...(hasSubscriptions && { subscriptions: subscriptionFields.join(symbol) }),
  });
};

const MemberInfoReward = () => {
  const { slFeatureService } = useAppContext();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [isDesktop] = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const translateModelWithLanguage = curryRight(translateModel)(i18n.language);
  const { userData } = useProfileContext();
  const memberInfoReward = userData.member_info_reward;
  const hasMemberInfoReward =
    slFeatureService.hasFeature('member_info_reward') &&
    Boolean(memberInfoReward);
  const hasMemberInfoRewardSubscription = slFeatureService.hasFeature(
    'member_info_reward_subscription',
  );

  if (!hasMemberInfoReward) {
    return null;
  }
  return (
    <Flex
      gap="10px"
      direction="column"
      data-e2e-id="member-center-profile-info-reward_container"
      w={isDesktop ? '50%' : '100%'}
      padding="0 10 20"
    >
      <Flex
        gap="14px"
        alignItems="center"
        className="member-center-profile-section_title"
      >
        <UserIcon
          fontSize="20px"
          color="#dddddd"
          className="member-center-profile__section-title-icon"
        />
        <Text
          variant="sectionTitle"
          className="member-center-profile__section-title-text"
        >
          {t('member_profile.member_info.title')}
        </Text>
      </Flex>
      <Text
        textAlign="left"
        variant="description"
        className="member-center-profile__description-text member-info-reward__text"
      >
        {getMemberInfoRewardDescription(
          memberInfoReward,
          t,
          translateModelWithLanguage,
          hasMemberInfoRewardSubscription,
        )}
      </Text>
    </Flex>
  );
};

export default MemberInfoReward;
